import { useState, useEffect } from 'react';

/**
 * Platform detection hook for Capacitor apps
 * Detects platform, device type, and provides platform-specific utilities
 */
export const usePlatform = () => {
  const [platform, setPlatform] = useState({
    isCapacitor: false,
    platform: 'web',
    isAndroid: false,
    isIOS: false,
    isWeb: true,
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    hasSafeArea: false,
    statusBarHeight: 0,
    hasNotch: false,
    isStandalone: false,
  });

  useEffect(() => {
    const detectPlatform = () => {
      const isCapacitor = typeof window !== 'undefined' && window.Capacitor;
      const userAgent = navigator.userAgent.toLowerCase();
      
      let platform = 'web';
      let isAndroid = false;
      let isIOS = false;
      let isMobile = false;
      let isTablet = false;
      let isDesktop = false;
      let hasSafeArea = false;
      let statusBarHeight = 0;
      let hasNotch = false;
      let isStandalone = false;

      if (isCapacitor) {
        // Capacitor environment
        const capacitorPlatform = window.Capacitor.getPlatform();
        platform = capacitorPlatform;
        
        if (capacitorPlatform === 'android') {
          isAndroid = true;
          isMobile = true;
          statusBarHeight = 24; // Default Android status bar height
          hasSafeArea = true;
        } else if (capacitorPlatform === 'ios') {
          isIOS = true;
          isMobile = true;
          statusBarHeight = 44; // Default iOS status bar height
          hasSafeArea = true;
          hasNotch = true; // Most modern iOS devices have notch
        }
      } else {
        // Web environment
        isAndroid = /android/i.test(userAgent);
        isIOS = /iphone|ipad|ipod/i.test(userAgent);
        isMobile = isAndroid || isIOS || /mobile/i.test(userAgent);
        isTablet = /ipad|tablet/i.test(userAgent) || 
                  (isAndroid && !/mobile/i.test(userAgent));
        isDesktop = !isMobile && !isTablet;
        
        // Check if running as PWA
        isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                      window.navigator.standalone === true;
      }

      setPlatform({
        isCapacitor,
        platform,
        isAndroid,
        isIOS,
        isWeb: !isCapacitor,
        isMobile,
        isTablet,
        isDesktop,
        hasSafeArea,
        statusBarHeight,
        hasNotch,
        isStandalone,
      });
    };

    detectPlatform();
  }, []);

  return {
    ...platform,
    
    // Platform-specific CSS classes
    getPlatformClasses: () => {
      const classes = [];
      
      if (platform.isAndroid) {
        classes.push('platform-android');
      }
      if (platform.isIOS) {
        classes.push('platform-ios');
      }
      if (platform.hasSafeArea) {
        classes.push('has-safe-area');
      }
      if (platform.hasNotch) {
        classes.push('has-notch');
      }
      
      return classes.join(' ');
    },
    
    // Platform-specific styles
    getPlatformStyles: () => {
      const styles = {};
      
      if (platform.hasSafeArea) {
        styles.paddingTop = `${platform.statusBarHeight}px`;
      }
      
      return styles;
    },
    
    // Platform-specific utilities
    getSafeAreaPadding: () => ({
      paddingTop: platform.hasSafeArea ? `${platform.statusBarHeight}px` : '0px',
      paddingBottom: platform.hasSafeArea ? 'env(safe-area-inset-bottom)' : '0px',
    }),
    
    // Platform-specific behavior flags
    shouldUseNativeScrolling: platform.isCapacitor,
    shouldUseNativeKeyboard: platform.isCapacitor,
    shouldUseNativeStatusBar: platform.isCapacitor,
  };
};

export default usePlatform;
